import React, { useState, useEffect, useMemo, useRef } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { getRandomInt, sleep } from "../shared/util";
import { Container } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";
import "./LetterFlies.scss";
import icon from "./letterflies-icon.png";
import { startAnimation, stopAnimation } from "../shared/animate";
import { getDistractors } from "./distractor";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen } from "Services/Listen";

import Letterfly from "./Letterfly";
import BugNet from "./BugNet";
import { getUserData } from "Services/API";
import { ipaToAmirabet } from "../../../app/services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

const EVENTS = {
  start: "start",
  retry: "retry",
};

export default function LetterFliesValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.letter_flies;

  const { id } = useVersion(props.wordMetadata, {
    1: ["PHON", "GRAPHEMIC_BREAKDOWN"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: LetterFlies1,
  }[versionId];

  return <Version {...props} 
    close={close}
    dispatchEvent={dispatchEvent}
    isMountedRef={isMountedRef}
  />;

  function dispatchEvent(event) {
    props.handleMessage({
      event,
    });
  }

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function LetterFlies1(props) {
  const { wordMetadata, speech, ftue, tutorMode, dispatchEvent, isInstruction, word, paused} = props;
  const { PHON, GRAPHEMIC_BREAKDOWN, targetGrapheme, example } = wordMetadata;


  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [giveAnswer, setGiveAnswer] = useState(false);
  const [hideDistractors, setHideDistractors] = useState(false);
  const [clickedOnCorrect, setClickedOnCorrect] = useState(null);

  const { speak, speakSequence, speakFile, speakPhonemes, listenPhoneme } =
    useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const [timer, setTimer] = useState(null);
  const didClick = useRef(false);
  const interactive = useRef(false);

  const letterFlies = useMemo(generateLetterflies, []);
  const { options, target, targetIndex } = letterFlies;

  function generateLetterflies() {
    if(targetGrapheme) return createLetterfliesFromMetadata();
    return createLetterflyRandomly();
  }

  function createLetterflyRandomly() {
    const targetIndex = getRandomInt(0, graphemes.length);
    const target = graphemes[targetIndex];
    const distractors = getDistractors(target, graphemes);
    const options = [...distractors, target];
    return { options, target, targetIndex };
  }

  function createLetterfliesFromMetadata() {
    const targetIndex = graphemes.findIndex((g) => g === targetGrapheme);
    const target = targetGrapheme;
    const options = [...graphemes];
    return { options, target, targetIndex };
  }

  const userLocale = useMemo(() => getUserData().locale, []);
  const files = useMemo(() => getAudioForPhonemes(PHON, userLocale), [PHON, userLocale]);
  
  const sound = files[targetIndex];

  useEffect(() => {
    startAnimation();
    return () => {
      clearTimeout(timer)
      stopAnimation;
    }
  }, []);

  useEffect(() => {
    if (paused) {
      setStage(3);
    }
  }, [paused]);

  // handle click on letterfly and go to next stage (2)
  useEffect(() => {
    if (clickedOnCorrect !== null && stage === 1) {
      setStage(2);
    }
  }, [clickedOnCorrect, stage]);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage, isInstruction]);

  async function onStageChange() {
    if (isInstruction || !props.isMountedRef.current) {
      return;
    }

    switch (stage) {
      case 0: await HandleStageZero(); break;
      case 1: await HandleStageOne(); break;
      case 2: await handleStageTwo(); break;
      case 3: await handleStageThree(); break;
    }
  }

  // FTUE only stage - FTUE is defined as if story of this type is served for the first time in the current session.
  async function HandleStageZero() {
    try {
      if (tutorMode) {
        speech.stopListening();
        await cancellablePromise(speak(ttsStrings.tutor.intro))
        await cancellablePromise(sleep());
      } else {
        await cancellablePromise(speak(ttsStrings.intro));
      }
      await cancellablePromise(sleep());
      await cancellablePromise(speakPhonemes({ phon: example ? example.phon : "æ" }));
      await cancellablePromise(sleep());
      setGiveAnswer(true);
      await cancellablePromise(sleep(3000));
      setGiveAnswer(false);
      setStage(1);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  // Non-FTUE will start from this stage
  async function HandleStageOne() {
    try {
      if (ftue && !tutorMode) await cancellablePromise(speak(ttsStrings.your_turn));

      // baked in instructions for the first item, we wanted to play this the story metadata items.instructions.verbalInstructions,
      // but it requires a lot of refactoring to get it to work, so we work this around by baking it in here for now.
      if (tutorMode) {
        if(ftue) {
          await cancellablePromise(speak(ttsStrings.tutor.your_turn_instruction_1));
        }
        if (getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            { textToSpeak: word, skipTranslation: true }, // [word]
            ttsStrings.tutor.your_turn_instruction_2, // has the
          ]));
          await cancellablePromise(speakFile(sound)); // [phoneme]
          interactive.current = true; // start clickable
          !didClick.current && await cancellablePromise(speak(ttsStrings.tutor.your_turn_instruction_3)) // which letter has the
          !didClick.current && await cancellablePromise(speakFile(sound)); // [phoneme]
          !didClick.current && await cancellablePromise(speak(ttsStrings.tutor.your_turn_instruction_4)) // sound.
        } else {
          // await speak(`${ttsStrings.tutor.your_turn_instruction_1} ${word} ${ttsStrings.tutor.your_turn_instruction_2} <phoneme voice="human" alphabet="ipa" ph="${abPhonemes[targetIndex]}"/> ${ttsStrings.tutor.your_turn_instruction_3} <phoneme voice="human" alphabet="ipa" ph="${abPhonemes[targetIndex]}"/> ${ttsStrings.tutor.your_turn_instruction_4}`)
          // speak() doesn't work if there is voice="human" in the phoneme tag. So we have to split it up for now.
          await cancellablePromise(speak(`"${word}", ${ttsStrings.tutor.your_turn_instruction_2}`)) // has the
          await cancellablePromise(speakFile(sound)); // [phoneme]
          interactive.current = true; // start clickable
          !didClick.current && await cancellablePromise(speak(ttsStrings.tutor.your_turn_instruction_3)) // which letter has the
          !didClick.current && await cancellablePromise(speakFile(sound)); // [phoneme]
          !didClick.current && await cancellablePromise(speak(ttsStrings.tutor.your_turn_instruction_4)) // sound.
        }
      }else{
        interactive.current = true; // start clickable
      }

      !didClick.current && await cancellablePromise(speak(ttsStrings.catch_butterfly)); // the sound to catch is
      !didClick.current && await cancellablePromise(sleep(500));
      !didClick.current && await cancellablePromise(speakFile(sound)); // [phoneme]

      !didClick.current && await cancellablePromise(speak(ttsStrings.click_on)); // click on
      const sounds = [sound, sound, sound]
      for await (const sound of sounds) {
        await cancellablePromise(sleep(500));
        !didClick.current && await cancellablePromise(speakFile(sound)); // speak [phoneme] 3 times
      }

      // handler for first non click action
      const handleNoActions = () => {
        if (!props.isMountedRef.current) return;
        // handler for second non click action
        const handleFinalNoClickAction = () => {
          !didClick.current && setTimer(setTimeout(onNoClickAction2, 10000));
        }
        onNoClickAction(sound, handleFinalNoClickAction)
      }

      // if still no click after 10 seconds, start timer to handle it.
      !didClick.current && setTimer(setTimeout(handleNoActions, 10000));
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function handleStageTwo() {
    try {
      clearTimeout(timer);
      setTimer(null);
      
      if (clickedOnCorrect) {
        // correct
        setGiveAnswer(true);
        await cancellablePromise(speak(ttsStrings.correct_1)); // you caught that letterfly! // WHY there was a second abort and Aborted this?
        await cancellablePromise(promptSaySound()); // Now say the sound x makes.
        await cancellablePromise(handleStageTwoContinued());
      } else {
        // incorrect
        await cancellablePromise(onIncorrect());
        await cancellablePromise(promptSaySound());
        await cancellablePromise(handleStageTwoContinued());
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function handleStageTwoContinued() {
    try {
      if (!props.isMountedRef.current) return;

      if(tutorMode) {
        // tutor mode behavior
        // dispatch start event to start the phrase
        dispatchEvent(EVENTS.start);
      }else{
        // default intervention behavior
        const { status } = await cancellablePromise(listenPhoneme({
          file: sound,
          targetWord: abPhonemes[targetIndex],
        }));
        if (status === "skip") return props.close();
        await cancellablePromise(speak(ttsStrings.correct_2));
        props.close(); 
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function handleStageThree() {
    // handle silent in tutor mode
    if(tutorMode) { 
      handleTutorModeSilent();
    }
  }

  async function handleTutorModeSilent(){
    if (!props.isMountedRef.current) return;

    try {
      speech.stopListening();
      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.tutor.silent_instruction_1,
          { textToSpeak: target, skipTranslation: true },
          ttsStrings.tutor.silent_instruction_2,
          { textToSpeak: target, skipTranslation: true },
        ]));
      }else{
        await cancellablePromise(speak(`${ttsStrings.tutor.silent_instruction_1} "${target}" ${ttsStrings.tutor.silent_instruction_2}`));
      }
      speech.startListening();
      // notify parent to that retry started
      props.dispatchEvent(EVENTS.retry);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function promptSaySound(){
    if (!props.isMountedRef.current) return;

    try {
      const targetWord = target.split("").join(" ");
      if (getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.ask_sound_part_1,
          { textToSpeak: `<prosody rate="slow">${targetWord}</prosody>`, skipTranslation: true },
          ttsStrings.ask_sound_part_2,
        ]));
      } else {
        await cancellablePromise(speak(
          `${ttsStrings.ask_sound_part_1} "${targetWord}" ${ttsStrings.ask_sound_part_2}`
        ));
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon} className="letterflies-container">
      {renderContent()}
      <BugNet visible={giveAnswer} />
      {!tutorMode &&
      <div style={{ position: "absolute", bottom: 30 }}>
        <ListeningStatus />
      </div>}
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      const exampleWord = (example && example.GRAPHEMIC_BREAKDOWN) ? example.GRAPHEMIC_BREAKDOWN.split("/") : ["c", "a", "t"]
      const exampleTargetGrapheme = (example && example.targetGrapheme) ? example.targetGrapheme : "a"
      return (
        <div className="letterfly-grid">
          {exampleWord.map((grapheme, i) => {
            const style = {};
            const isCorrect = grapheme === exampleTargetGrapheme;
            if (isCorrect) style.zIndex = 3;
            const isHidden = giveAnswer && !isCorrect;
            const isCaught = giveAnswer && isCorrect;
            const id = `demo-${i}`;
            return (
              <Letterfly
                key={id}
                id={id}
                grapheme={grapheme}
                style={style}
                caught={isCaught}
                hidden={isHidden}
                disabled
              />
            );
          })}
        </div>
      );
    }

    return (
      <div className="letterfly-grid">
        {options.map((grapheme, i) => {
          const style = {};
          const isCorrect = grapheme === target;
          if (isCorrect) style.zIndex = 3;
          const isHidden = (giveAnswer || hideDistractors) && !isCorrect;
          const isCaught = giveAnswer && isCorrect;
          return (
            <Letterfly
              key={i}
              id={i}
              grapheme={grapheme}
              style={style}
              caught={isCaught}
              disabled={!interactive.current}
              onClick={() => onClick(isCorrect)}
              hidden={isHidden}
            />
          );
        })}
      </div>
    );
  }

  async function onClick(isCorrect){
    if(!interactive.current) return; // make sure we allow when ready
    if(clickedOnCorrect !== null) return; // make sure we allow only one click
    didClick.current = true;
    interactive.current = false;
    await speech.abortSpeech(async ()=>{
      setClickedOnCorrect(isCorrect);
    });
  }

  async function onNoClickAction(sound, next) {
    if (!props.isMountedRef.current) return;

    try {
      speech.abortSpeech(async () => {
        await cancellablePromise(speak(ttsStrings.tutor.on_no_click_action_1));
        await cancellablePromise(speakFile(sound));
        await cancellablePromise(speak(ttsStrings.tutor.on_no_click_action_2));
        next();
      });
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onNoClickAction2(){
    if (!props.isMountedRef.current) return;

    if(stage === 1) {
      setStage(2);
    }
  }

  async function onIncorrect() {
    if (!props.isMountedRef.current) return;

    try {
      interactive.current = false;
      setHideDistractors(true);
      await cancellablePromise(speak(ttsStrings.struggle));
      if(stage === 1) {
        setStage(2);
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }
}
